import { WosbConnection } from "./wosb-connection";
import { OrderChangeInfo } from "./wosb-connection-contracts";

export interface OrderChangeQueue {
  enqueue(change: OrderChangeInfo): void;
  dequeue(): Promise<OrderChangeInfo>;
  getCount(): number;
  addChangeListener(callback: (enqueuedItemsCount: number) => void): void;
  dispose(): void;
}

export class InMemoryOrderChangeQueue implements OrderChangeQueue {
  queue: OrderChangeInfo[] = [];
  resolveCurrentDequeueOperation: ((change: OrderChangeInfo) => void) | null =
    null;
  changeListeners: ((enqueuedItemsCount: number) => void)[] = [];
  isDisposed: boolean = false;

  enqueue(change: OrderChangeInfo): void {
    this.ensureNotDisposed();
    if (!change) {
      throw new Error("OrderChangeInfo is not allowed to be null");
    }
    if (this.resolveCurrentDequeueOperation === null) {
      this.queue.push(change);
      this.alertChangeListeners(this.queue.length);
    } else {
      this.alertChangeListeners(1);
      this.resolveCurrentDequeueOperation(change);
      this.resolveCurrentDequeueOperation = null;
      this.alertChangeListeners(0);
    }
  }

  dequeue(): Promise<OrderChangeInfo> {
    this.ensureNotDisposed();
    if (this.resolveCurrentDequeueOperation !== null) {
      throw new Error(
        "Not allowed to have multiple dequeue operations ongoing at the same time"
      );
    }
    if (this.queue.length > 0) {
      const item = this.queue.shift();
      this.alertChangeListeners(this.queue.length);
      return Promise.resolve(item);
    } else {
      return new Promise((resolve) => {
        this.resolveCurrentDequeueOperation = resolve;
      });
    }
  }

  getCount(): number {
    this.ensureNotDisposed();
    return this.queue.length;
  }

  addChangeListener(callback: (enqueuedItemsCount: number) => void): void {
    if (!callback) {
      throw new Error("Callback function is not allowed to be null");
    }
    this.ensureNotDisposed();
    this.changeListeners.push(callback);
  }

  dispose(): void {
    if (this.isDisposed) {
      return;
    }

    this.isDisposed = true;
    this.changeListeners = []
    if (this.resolveCurrentDequeueOperation !== null) {
      this.resolveCurrentDequeueOperation(null);
    }
  }

  private alertChangeListeners(queuedItemCount: number) {
    if (this.changeListeners) {
      this.changeListeners.forEach(callback => {
        try {
          callback(queuedItemCount);
        } catch (error) {
          reportError(error);
        }
      });
    }
  }

  private ensureNotDisposed(): void {
    if (this.isDisposed) {
      throw new Error("Cannot perform this operation on a disposed order change queue.");
    }
  }
}

export const createOrderChangeQueue = async (connection: WosbConnection): Promise<OrderChangeQueue> => {
  if (!connection.isConnected) {
    throw new Error("Cannot create an order change queue for a non-connected wosbconnection");
  }
  const queue = new InMemoryOrderChangeQueue();

  connection.addOrderChangeListener(change => queue.enqueue(change));
  const currentOrders = await connection.getOrders();
  for (let i = 0; i < currentOrders.length; i++) {
    queue.enqueue({
      type: 'added',
      order: currentOrders[i]
    });
  }
  return queue;
};